import React from 'react';
import {
  Card,
  CardMedia,
  Box,
  Typography,
  IconButton,
  Button,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import AlbumIcon from '@mui/icons-material/Album';
import { Timestamp } from 'firebase/firestore';
import { Rating, WatchlistMovie, getRating } from '../Watchlist';
import { cardFoot } from '../../../utils/surfaces';
import { brand } from '../../../theme/colors';

interface WatchlistCardProps {
  movie: WatchlistMovie;
  isToWatchTab: boolean;
  isReadOnly: boolean;
  // To Watch tab in My order, signed in
  draggable: boolean;
  // Formats owned on disc, from the physical movie collection (To Watch only).
  ownedFormats?: string[];
  onMenuClick: (
    e: React.MouseEvent<HTMLButtonElement>,
    movie: WatchlistMovie
  ) => void;
  onMarkAsWatched: (movie: WatchlistMovie) => void;
  onCycleRating: (movie: WatchlistMovie) => void;
  onOpenImdb: (movie: WatchlistMovie) => void;
}

const overlayButton = {
  position: 'absolute',
  top: 4,
  bgcolor: 'rgba(0,0,0,0.55)',
  color: 'common.white',
  backdropFilter: 'blur(4px)',
  '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
} as const;

const RATING_ICONS: Record<Rating, { Icon: typeof StarIcon; color: string }> = {
  favourite: { Icon: StarIcon, color: 'primary.main' },
  disliked: { Icon: ThumbDownIcon, color: 'error.light' },
  none: { Icon: StarBorderIcon, color: 'text.disabled' },
};

// Tooltip names what the next tap does
const NEXT_ACTION: Record<Rating, string> = {
  none: 'Mark favourite',
  favourite: 'Mark disliked',
  disliked: 'Clear rating',
};

const formatDate = (ts: Timestamp | null) => {
  if (!ts) return '';
  return ts.toDate().toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const WatchlistCard = ({
  movie,
  isToWatchTab,
  isReadOnly,
  draggable,
  ownedFormats,
  onMenuClick,
  onMarkAsWatched,
  onCycleRating,
  onOpenImdb,
}: WatchlistCardProps) => {
  const isWatchedTab = !isToWatchTab;
  const rating = getRating(movie);
  const { Icon: RatingIcon, color: ratingColor } = RATING_ICONS[rating];

  // When read-only: only show bottom area if there's info worth showing
  // (watch count on Watched tab). No Mark button.
  const showBottomArea = isReadOnly
    ? movie.watchCount > 0
    : movie.watchCount > 0 || isToWatchTab;

  return (
    <Card
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        position: 'relative',
        border: '1px solid',
        borderColor: 'primary.main',
      }}
    >
      <CardMedia
        component="img"
        src={movie.image}
        alt={movie.title}
        onClick={() => onOpenImdb(movie)}
        sx={{
          width: '100%',
          aspectRatio: '2 / 3',
          objectFit: 'cover',
          display: 'block',
          cursor: 'pointer',
        }}
      />

      {/* Owned on disc: sits just left of the 3-dot menu */}
      {ownedFormats && (
        <Tooltip
          title={`Owned on ${ownedFormats.join(' & ')}`}
          enterTouchDelay={0}
        >
          <Box
            role="img"
            aria-label={`Owned on ${ownedFormats.join(' & ')}`}
            sx={{
              ...overlayButton,
              right: 40,
              width: 30,
              height: 30,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              color: brand.pink,
            }}
          >
            <AlbumIcon fontSize="small" />
          </Box>
        </Tooltip>
      )}

      {/* 3-dot menu — hidden in read-only mode */}
      {!isReadOnly && (
        <IconButton
          size="small"
          onClick={(e) => onMenuClick(e, movie)}
          sx={{ ...overlayButton, right: 4 }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      )}

      {/* Bottom area: info row + Mark as Watched button */}
      {showBottomArea && (
        <Box
          sx={(theme) => ({
            p: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            ...cardFoot(theme),
          })}
        >
          {/* Info row — dropped entirely when there's nothing to show */}
          <Box
            sx={{
              display: movie.watchCount > 0 ? 'flex' : 'none',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {/* Rating — visitors only see it once it's set */}
            {isWatchedTab &&
              (isReadOnly ? (
                rating !== 'none' && (
                  <RatingIcon sx={{ fontSize: 18, color: ratingColor }} />
                )
              ) : (
                <Tooltip title={NEXT_ACTION[rating]}>
                  <IconButton
                    size="small"
                    aria-label={NEXT_ACTION[rating]}
                    onClick={() => onCycleRating(movie)}
                    sx={{ p: 0, color: ratingColor }}
                  >
                    <RatingIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              ))}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: 'text.secondary',
                lineHeight: 0,
              }}
            >
              <VisibilityIcon fontSize="small" />
              <Typography variant="body2" sx={{ lineHeight: 1 }}>
                {movie.watchCount}
              </Typography>
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: 'auto' }}
            >
              {formatDate(movie.lastWatched)}
            </Typography>
          </Box>

          {isToWatchTab && !isReadOnly && (
            <Button
              fullWidth
              size="small"
              variant="contained"
              color="primary"
              onClick={() => onMarkAsWatched(movie)}
              sx={{ fontSize: '0.7rem' }}
            >
              Mark as Watched
            </Button>
          )}
        </Box>
      )}
    </Card>
  );
};

export default WatchlistCard;